import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconArrowLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function IconWarning() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function IconInfo() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function IconPR() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="18" r="3" /><circle cx="6" cy="6" r="3" />
      <path d="M13 6h3a2 2 0 0 1 2 2v7" />
      <line x1="6" y1="9" x2="6" y2="21" />
    </svg>
  );
}

function IconUndo() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 .49-4.68" />
    </svg>
  );
}

// ── Step indicator ────────────────────────────────────────────────────────────

function StepIndicator({ current }) {
  const steps = [
    { n: 1, label: 'Input PRs' },
    { n: 2, label: 'Map Repos' },
    { n: 3, label: 'Preview' },
    { n: 4, label: 'Done' },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 28 }}>
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700,
              background: current === s.n
                ? 'var(--accent)'
                : current > s.n ? 'var(--success, #22c55e)' : 'var(--surface-2, #1e293b)',
              color: current >= s.n ? '#fff' : 'var(--text-muted)',
              border: current === s.n ? '2px solid var(--accent)' : '2px solid transparent',
              flexShrink: 0,
            }}>
              {current > s.n ? <IconCheck /> : s.n}
            </div>
            <span style={{
              fontSize: 13,
              fontWeight: current === s.n ? 600 : 400,
              color: current === s.n ? 'var(--text-primary)' : 'var(--text-muted)',
            }}>
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div style={{
              flex: 1, height: 2, minWidth: 24, maxWidth: 50, margin: '0 8px',
              background: current > s.n ? 'var(--success, #22c55e)' : 'var(--border)',
            }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Collapsible info panel ────────────────────────────────────────────────────

function InfoPanel({ title, count, variant = 'warning', children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!count) return null;

  const colors = {
    warning: { bg: 'var(--warning-bg, #451a03)', border: 'var(--warning, #f59e0b)', text: 'var(--warning, #f59e0b)' },
    info:    { bg: 'var(--info-bg, #0c1a2e)',    border: 'var(--accent)',             text: 'var(--accent)'           },
    success: { bg: 'var(--success-bg, #052e16)', border: 'var(--success, #22c55e)',  text: 'var(--success, #22c55e)' },
    error:   { bg: 'var(--error-bg, #450a0a)',   border: 'var(--error, #ef4444)',    text: 'var(--error, #ef4444)'   },
  };
  const c = colors[variant] || colors.info;

  return (
    <div style={{
      border: `1px solid ${c.border}`, borderRadius: 8,
      background: c.bg, marginBottom: 12, overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', textAlign: 'left', padding: '10px 14px',
          background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 8,
          color: c.text, fontWeight: 600, fontSize: 13,
        }}
      >
        <IconWarning />
        {title} ({count})
        <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.7 }}>
          {open ? '▲ Hide' : '▼ Show'}
        </span>
      </button>
      {open && (
        <div style={{ padding: '0 14px 12px', borderTop: `1px solid ${c.border}` }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── URL list display ──────────────────────────────────────────────────────────

function UrlList({ items, labelKey = 'pr_url', secondaryKey }) {
  return (
    <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', fontSize: 12 }}>
      {items.slice(0, 20).map((item, i) => (
        <li key={i} style={{
          padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
          display: 'flex', gap: 8, alignItems: 'flex-start',
        }}>
          <span style={{ color: 'var(--text-muted)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {item[labelKey] || item}
          </span>
          {secondaryKey && item[secondaryKey] && (
            <span style={{ color: 'var(--text-secondary)', flexShrink: 0, fontSize: 11 }}>
              ({item[secondaryKey]})
            </span>
          )}
        </li>
      ))}
      {items.length > 20 && (
        <li style={{ padding: '6px 0', color: 'var(--text-muted)', fontSize: 11 }}>
          …and {items.length - 20} more
        </li>
      )}
    </ul>
  );
}

// A repository is identified by provider + full name, the same pair the
// repositories table is unique on: two providers can host "acme/voice".
function repoKey(r) {
  return `${r.provider}|${r.repo_full_name}`;
}

// ── Normaliser output helpers ─────────────────────────────────────────────────

const KIND_LABELS = {
  feature:          { label: 'Feature',   color: 'var(--success, #22c55e)' },
  bug_fix:          { label: 'Bug fix',   color: 'var(--warning, #f59e0b)' },
  refactor:         { label: 'Rewrite',   color: 'var(--accent)' },
  chore:            { label: 'Chore',     color: 'var(--text-muted)' },
  not_cataloguable: { label: 'Not cataloguable', color: 'var(--error, #ef4444)' },
};

// A row reaches the reviewer only when the model produced a usable entry.
// Anything else (Jira unreachable, model unavailable, empty output) is a
// failure and never appears as an editable row, so it has no entry here.
const REVIEW_REASONS = {
  task_language:    'Still reads like a task after one retry',
  not_cataloguable: 'Model could not name a capability',
};

function KindBadge({ kind }) {
  const k = KIND_LABELS[kind] || { label: kind || '—', color: 'var(--text-muted)' };
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4,
      padding: '2px 6px', borderRadius: 4, border: `1px solid ${k.color}`, color: k.color,
      whiteSpace: 'nowrap',
    }}>
      {k.label}
    </span>
  );
}

const fieldStyle = {
  width: '100%', fontSize: 12, padding: '5px 8px', margin: 0,
  background: 'var(--surface-2, #1e293b)', border: '1px solid var(--border)',
  borderRadius: 6, color: 'var(--text-primary)',
};

/**
 * One editable preview row: the catalogue entry Claude derived from a ticket,
 * with a checkbox, a placement control (new feature vs attach to an existing
 * one) and the model's rationale.
 */
function PreviewRow({ row, onToggle, onChange, onPlacement }) {
  const flagged = row.needs_review;
  const attaching = row.action === 'attach_to_existing';
  const placementValue = attaching ? row.related_feature_id : 'create_new';

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '28px 1fr', gap: 10,
      padding: '12px 14px', borderRadius: 8,
      border: `1px solid ${flagged ? 'var(--warning, #f59e0b)' : 'var(--border)'}`,
      background: row.selected ? 'transparent' : 'rgba(255,255,255,0.02)',
      opacity: row.selected ? 1 : 0.75,
    }}>
      <input
        type="checkbox"
        checked={!!row.selected}
        onChange={onToggle}
        style={{ width: 16, height: 16, marginTop: 4, cursor: 'pointer' }}
        aria-label="Include this row in the import"
      />

      <div style={{ minWidth: 0 }}>
        {/* Header line: ticket, kind, product, PR */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8, fontSize: 12 }}>
          <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.jira_ticket_id || '—'}</span>
          <KindBadge kind={row.kind} />
          {row.product_name && (
            <span style={{ color: 'var(--text-muted)' }}>{row.product_name}</span>
          )}
          {row.jira_parent && (
            <span style={{ color: 'var(--text-muted)' }} title="Epic / parent">
              ↳ {row.jira_parent}
            </span>
          )}
          <a href={row.pr_url} target="_blank" rel="noreferrer"
            style={{ marginLeft: 'auto', color: 'var(--accent)', fontSize: 11, fontFamily: 'monospace' }}>
            {row.pr_url.replace(/^https?:\/\//, '').slice(0, 48)}…
          </a>
        </div>

        {/* Original ticket, for comparison */}
        {row.jira_summary && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}
            title={row.pr_title}>
            Ticket: <span style={{ fontStyle: 'italic' }}>{row.jira_summary}</span>
          </div>
        )}

        {/* Editable entry */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
          <div>
            <label style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Major feature
            </label>
            <input style={fieldStyle} value={row.major_feature || ''}
              onChange={e => onChange({ major_feature: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Sub feature
            </label>
            <input style={fieldStyle} value={row.sub_feature || ''}
              onChange={e => onChange({ sub_feature: e.target.value })} />
          </div>
        </div>
        <div style={{ marginBottom: 8 }}>
          <label style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Description
          </label>
          <textarea style={{ ...fieldStyle, resize: 'vertical' }} rows={2} value={row.description || ''}
            onChange={e => onChange({ description: e.target.value })} />
        </div>

        {/* Placement */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', fontSize: 12 }}>
          <label style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Place
          </label>
          <select
            style={{ ...fieldStyle, width: 'auto', maxWidth: 420 }}
            value={placementValue}
            onChange={e => onPlacement(e.target.value)}
          >
            <option value="create_new">Create as a new feature</option>
            {(row.candidates || []).map(c => (
              <option key={c.feature_id} value={c.feature_id}>
                Attach to: {c.major_feature}{c.sub_feature ? ` / ${c.sub_feature}` : ''} ({Math.round(c.score * 100)}%)
              </option>
            ))}
          </select>
          {attaching && (
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
              No new row; the PR&apos;s files map to the existing feature.
            </span>
          )}
          {typeof row.confidence === 'number' && row.confidence > 0 && (
            <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 11 }}>
              confidence {Math.round(row.confidence * 100)}%
            </span>
          )}
        </div>

        {/* Rationale / review flag */}
        {(row.rationale || flagged || row.ai_error) && (
          <div style={{ marginTop: 8, fontSize: 11, color: flagged ? 'var(--warning, #f59e0b)' : 'var(--text-muted)' }}>
            {flagged && (
              <strong style={{ marginRight: 6 }}>
                Review: {REVIEW_REASONS[row.review_reason] || row.review_reason || 'flagged'}.
              </strong>
            )}
            {row.rationale}
            {row.ai_error && <span> ({row.ai_error})</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Backfill: re-normalise features imported before the normaliser existed ────

function RenormalizePanel({ products, onClose }) {
  const [productId, setProductId] = useState('');
  const [limit, setLimit]         = useState(25);
  const [loading, setLoading]     = useState(false);
  const [rows, setRows]           = useState(null);
  const [failed, setFailed]       = useState([]);
  const [meta, setMeta]           = useState(null);
  const [applying, setApplying]   = useState(false);
  const [result, setResult]       = useState(null);
  const [error, setError]         = useState('');

  async function preview() {
    setError(''); setResult(null); setFailed([]); setLoading(true);
    try {
      const res = await axios.post('/import/pr/renormalize/preview', {
        product_id: productId ? parseInt(productId, 10) : undefined,
        limit,
      });
      setFailed(res.data.failed || []);
      setRows((res.data.rows || []).map(r => ({
        ...r,
        selected: !r.needs_review,
        // the proposed text is what gets written; editable
        major_feature: r.proposed.major_feature,
        sub_feature:   r.proposed.sub_feature,
        description:   r.proposed.description,
      })));
      setMeta({ total: res.data.total_imported, model: res.data.model, cost: res.data.cost_usd });
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to build the preview.');
    } finally {
      setLoading(false);
    }
  }

  async function apply() {
    const chosen = (rows || []).filter(r => r.selected);
    if (!chosen.length) { setError('Select at least one row.'); return; }
    if (!window.confirm(`Overwrite the text of ${chosen.length} feature${chosen.length !== 1 ? 's' : ''} with the proposed entries?`)) return;
    setError(''); setApplying(true);
    try {
      const res = await axios.post('/import/pr/renormalize/apply', {
        updates: chosen.map(r => ({
          feature_pk: r.feature_pk,
          major_feature: r.major_feature, sub_feature: r.sub_feature, description: r.description,
        })),
      });
      setResult(res.data);
      setRows(rows.filter(r => !r.selected));
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to apply the changes.');
    } finally {
      setApplying(false);
    }
  }

  function update(i, patch) {
    setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  const selectedCount = (rows || []).filter(r => r.selected).length;

  return (
    <div>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
        Features imported from PRs before the normaliser existed still carry Jira task text.
        This re-reads each feature's original Jira ticket and shows the current text next to the
        proposed catalogue entry. A feature whose ticket cannot be read is skipped, not rewritten
        from its own text. Only rows you tick are overwritten; the PR and Jira links stay as they are.
        A suggestion to attach to another feature is shown for information — this panel rewrites
        text, it does not merge features.
      </p>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <label className="form-label" style={{ fontSize: 12 }}>Product (optional)</label>
          <select className="form-input" style={{ margin: 0, fontSize: 13, minWidth: 220 }}
            value={productId} onChange={e => setProductId(e.target.value)}>
            <option value="">All products</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label" style={{ fontSize: 12 }}>Batch size</label>
          <input className="form-input" type="number" min={1} max={100} style={{ margin: 0, width: 90, fontSize: 13 }}
            value={limit} onChange={e => setLimit(Math.max(1, Math.min(100, parseInt(e.target.value || '1', 10))))} />
        </div>
        <button className="btn btn-primary" onClick={preview} disabled={loading}>
          {loading ? 'Normalising…' : 'Preview changes'}
        </button>
        <button className="btn btn-ghost" onClick={onClose}>Back to import</button>
      </div>

      {meta && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
          {meta.total} PR-imported feature{meta.total !== 1 ? 's' : ''} in the registry
          {rows ? ` · showing ${rows.length}` : ''}
          {meta.model ? ` · ${meta.model}` : ''}
          {meta.cost ? ` · $${Number(meta.cost).toFixed(4)}` : ''}
        </div>
      )}

      {result && (
        <div className="alert alert-success" style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
          <IconCheck /> Updated {result.updated} feature{result.updated !== 1 ? 's' : ''}.
          {result.skipped?.length ? ` ${result.skipped.length} skipped.` : ''}
        </div>
      )}
      {error && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>
          <IconWarning /> {error}
        </div>
      )}

      {failed.length > 0 && (
        <InfoPanel
          title="Skipped — the original Jira ticket could not be read"
          count={failed.length}
          variant="error"
          defaultOpen
        >
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '8px 0 8px' }}>
            These are left exactly as they are. Rewriting a feature from its own task text would
            only reword the ticket framing that needs replacing, so the ticket itself is required.
          </p>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: 12 }}>
            {failed.map((f, i) => (
              <li key={i} style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ fontFamily: 'monospace', color: 'var(--accent)', marginRight: 8 }}>
                  {f.feature_id}
                </span>
                <span style={{ fontFamily: 'monospace', color: 'var(--error, #ef4444)', marginRight: 8 }}>
                  {f.jira_ticket_id || '(no ticket)'}
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>{f.hint || f.reason}</span>
              </li>
            ))}
          </ul>
        </InfoPanel>
      )}

      {rows && rows.length === 0 && failed.length === 0 && !result && (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Nothing to re-normalise for this selection.</p>
      )}

      {rows && rows.length > 0 && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {rows.map((r, i) => (
              <div key={r.feature_pk} style={{
                display: 'grid', gridTemplateColumns: '28px 1fr', gap: 10, padding: '12px 14px', borderRadius: 8,
                border: `1px solid ${r.needs_review ? 'var(--warning, #f59e0b)' : 'var(--border)'}`,
                opacity: r.selected ? 1 : 0.75,
              }}>
                <input type="checkbox" checked={!!r.selected} onChange={() => update(i, { selected: !r.selected })}
                  style={{ width: 16, height: 16, marginTop: 4, cursor: 'pointer' }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8, fontSize: 12 }}>
                    <span style={{ fontFamily: 'monospace', color: 'var(--accent)' }}>{r.feature_id}</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.jira_ticket_id || '—'}</span>
                    <KindBadge kind={r.kind} />
                    {r.product_name && <span style={{ color: 'var(--text-muted)' }}>{r.product_name}</span>}
                    {!r.jira_fetched && (
                      <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(Jira unavailable — normalised from current text)</span>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Current</div>
                      <div><strong style={{ color: 'var(--text-secondary)' }}>{r.current.major_feature}</strong>
                        {r.current.sub_feature ? ` / ${r.current.sub_feature}` : ''}</div>
                      <div style={{ marginTop: 4, fontStyle: 'italic' }}>{r.current.description}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Proposed</div>
                      <input style={{ ...fieldStyle, marginBottom: 6 }} value={r.major_feature}
                        onChange={e => update(i, { major_feature: e.target.value })} />
                      <input style={{ ...fieldStyle, marginBottom: 6 }} value={r.sub_feature}
                        onChange={e => update(i, { sub_feature: e.target.value })} placeholder="Sub feature" />
                      <textarea style={{ ...fieldStyle, resize: 'vertical' }} rows={3} value={r.description}
                        onChange={e => update(i, { description: e.target.value })} />
                    </div>
                  </div>

                  {(r.rationale || r.needs_review || r.action === 'attach_to_existing') && (
                    <div style={{ marginTop: 8, fontSize: 11, color: r.needs_review ? 'var(--warning, #f59e0b)' : 'var(--text-muted)' }}>
                      {r.needs_review && (
                        <strong style={{ marginRight: 6 }}>
                          Review: {REVIEW_REASONS[r.review_reason] || r.review_reason || 'flagged'}.
                        </strong>
                      )}
                      {r.action === 'attach_to_existing' && (
                        <span style={{ marginRight: 6 }}>
                          Suggests this belongs under <span style={{ fontFamily: 'monospace' }}>{r.related_feature_id}</span>.
                        </span>
                      )}
                      {r.rationale}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={apply} disabled={applying || selectedCount === 0}>
              {applying ? 'Applying…' : `Overwrite ${selectedCount} feature${selectedCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main wizard ───────────────────────────────────────────────────────────────

export default function PRImportWizard() {
  const navigate  = useNavigate();
  const [step, setStep]   = useState(1);
  const [error, setError] = useState('');
  const runIdRef = useRef(null);

  // Step 1 state
  const [prInput, setPrInput]     = useState('');
  const [parsing, setParsing]     = useState(false);
  const [parseResult, setParseResult] = useState(null);
  // { eligible, no_jira_key, already_processed, invalid_urls, repositories, summary }

  // Step 2 state
  const [products, setProducts]       = useState([]);
  // { "provider|repo_full_name": product_id } — a repo belongs to one product,
  // and once saved on the repo the wizard never asks about it again.
  const [mappings, setMappings]       = useState({});
  const [savingMappings, setSavingMappings] = useState(false);

  // Step 3 state
  const [previewing, setPreviewing]   = useState(false);
  const [previewRows, setPreviewRows] = useState([]);   // editable; each row carries `selected`
  const [previewMeta, setPreviewMeta] = useState(null); // { model, cost_usd, usage, summary }
  const [failedRows, setFailedRows]   = useState([]);   // cannot be imported at all
  const [configError, setConfigError] = useState(null); // Jira/Anthropic not set up
  const [embeddingsConfigured, setEmbeddingsConfigured] = useState(true);
  const [jiraAuthError, setJiraAuthError] = useState(false);

  // Backfill panel (re-normalise features imported before the normaliser existed)
  const [cleanupMode, setCleanupMode] = useState(false);

  // Step 4 state
  const [executing, setExecuting]     = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [rolledBack, setRolledBack]   = useState(false);

  useEffect(() => {
    axios.get('/products', { params: { limit: 500 } })
      .then(res => setProducts(res.data.data || []))
      .catch(() => {});
  }, []);

  // ── Step 1: Parse PR URLs ─────────────────────────────────────────────────

  async function handleParse() {
    setError('');
    const urls = prInput.split('\n').map(u => u.trim()).filter(Boolean);
    if (!urls.length) {
      setError('Please paste at least one pull request URL.');
      return;
    }
    setParsing(true);
    try {
      const res = await axios.post('/import/pr/parse', { pr_urls: urls });
      setParseResult(res.data);

      // Pre-fill from repos that already have a product, so the step only
      // asks about the ones nobody has answered yet.
      const initial = {};
      for (const r of (res.data.repositories || [])) {
        if (r.product_id) initial[repoKey(r)] = r.product_id;
      }
      setMappings(initial);

      if ((res.data.eligible || []).length === 0) {
        // Nothing new to import — stay on step 1 but show the result.
        return;
      }
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to parse PR URLs. Please try again.');
    } finally {
      setParsing(false);
    }
  }

  // ── Step 2: Save repository → product mappings then preview ──────────────

  function allReposMapped() {
    return (parseResult?.repositories || []).every(r => mappings[repoKey(r)]);
  }

  async function handleSaveMappingsAndPreview() {
    setError('');
    if (!allReposMapped()) {
      setError('Please assign a product to every repository before continuing.');
      return;
    }
    setSavingMappings(true);
    try {
      // Saved on the repository itself, so this is the last time any of these
      // repos is asked about.
      const mappingList = (parseResult?.repositories || []).map(r => ({
        provider:       r.provider,
        repo_full_name: r.repo_full_name,
        sample_pr_url:  r.sample_pr_url,
        product_id:     parseInt(mappings[repoKey(r)], 10),
      }));
      await axios.post('/import/pr/repo-mappings', { mappings: mappingList });
    } catch {
      setError('Failed to save repository mappings. Please try again.');
      setSavingMappings(false);
      return;
    }

    // The product is read back from each repository during preview, so the
    // PR list is sent as parsed, carrying no product of its own.
    const prsToPreview = parseResult?.eligible || [];

    setSavingMappings(false);
    setConfigError(null);
    setFailedRows([]);
    setPreviewing(true);
    setStep(3);

    try {
      const res = await axios.post('/import/pr/preview', { prs: prsToPreview });
      // Rows the normaliser could not turn into a clean capability start
      // unticked: raw ticket text must be a deliberate choice, not the default.
      setPreviewRows((res.data.preview || []).map(r => ({
        ...r,
        selected: !r.needs_review && r.kind !== 'not_cataloguable',
      })));
      setPreviewMeta({
        model:    res.data.model,
        cost_usd: res.data.cost_usd,
        usage:    res.data.usage,
        summary:  res.data.summary,
      });
      setFailedRows(res.data.failed || []);
      setEmbeddingsConfigured(res.data.embeddings_configured !== false);
      setJiraAuthError(!!res.data.jira_auth_error);
    } catch (e) {
      const d = e.response?.data;
      // A missing Jira or Anthropic connection fails the whole request now,
      // rather than importing placeholder rows. Say which one, and where to fix it.
      if (d?.reason) setConfigError({ reason: d.reason, message: d.error });
      else setError(d?.error || 'Failed to generate preview. Please try again.');
    } finally {
      setPreviewing(false);
    }
  }

  // ── Step 4: Execute import ────────────────────────────────────────────────

  function updateRow(i, patch) {
    setPreviewRows(rows => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  function setPlacement(i, value) {
    // value: 'create_new' or the feature_id of a candidate to attach to.
    const row = previewRows[i];
    if (value === 'create_new') {
      updateRow(i, { action: 'create_new', related_feature_id: '', related_feature_pk: null });
      return;
    }
    const c = (row.candidates || []).find(x => x.feature_id === value);
    if (c) {
      updateRow(i, { action: 'attach_to_existing', related_feature_id: c.feature_id, related_feature_pk: c.feature_pk });
    }
  }

  const selectedRows = previewRows.filter(r => r.selected);

  async function handleExecute() {
    setError('');
    if (!selectedRows.length) {
      setError('Select at least one row to import.');
      return;
    }
    const run_id = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36);
    runIdRef.current = run_id;
    setExecuting(true);
    try {
      const res = await axios.post('/import/pr/execute', {
        features: selectedRows.map(({ selected, candidates, ...r }) => r),
        run_id,
      });
      setImportResult(res.data);
      setStep(4);
    } catch (e) {
      setError(e.response?.data?.error || 'Import failed. Please try again.');
    } finally {
      setExecuting(false);
    }
  }

  // ── Rollback ──────────────────────────────────────────────────────────────

  async function handleRollback() {
    if (!importResult?.run_id) return;
    if (!window.confirm('This will delete the features created in this run and unmap the PRs it attached to existing features. The same PR URLs will become eligible for future import. Continue?')) return;
    setRollingBack(true);
    try {
      await axios.post(`/import/pr/rollback/${importResult.run_id}`);
      setRolledBack(true);
    } catch (e) {
      setError(e.response?.data?.error || 'Rollback failed. Please try again.');
    } finally {
      setRollingBack(false);
    }
  }

  // ── Render helpers ────────────────────────────────────────────────────────

  const eligible        = parseResult?.eligible        || [];
  const noJiraKey       = parseResult?.no_jira_key     || [];
  const alreadyDone     = parseResult?.already_processed || [];
  const invalidUrls     = parseResult?.invalid_urls    || [];
  const repositories    = parseResult?.repositories    || [];
  const unmappedRepos   = repositories.filter(r => !r.product_id);

  // ── Step 1 ────────────────────────────────────────────────────────────────

  function renderStep1() {
    return (
      <div>
        <div style={{ marginBottom: 20 }}>
          <label className="form-label" style={{ marginBottom: 6 }}>
            Pull Request URLs
          </label>
          <textarea
            className="form-input"
            rows={14}
            style={{ fontFamily: 'monospace', fontSize: 12, resize: 'vertical', width: '100%' }}
            placeholder={
              'Paste one PR / MR URL per line. Supports hundreds of URLs.\n\n' +
              'Examples:\n' +
              '  https://github.com/acme/telephony/pull/1234\n' +
              '  https://bitbucket.org/acme/voice-service/pull-requests/567\n' +
              '  https://gitlab.company.com/eng/core/-/merge_requests/89\n' +
              '  https://gitea.company.com:3000/acme/messaging/pulls/12'
            }
            value={prInput}
            onChange={e => { setPrInput(e.target.value); setParseResult(null); setError(''); }}
          />
          <p className="form-hint" style={{ marginTop: 6 }}>
            The wizard will parse Jira ticket numbers (e.g. EN-3342, SL-2214) from PR titles and
            skip any PRs that have already been imported.
          </p>
        </div>

        {/* Results from a previous parse attempt (no eligible PRs) */}
        {parseResult && eligible.length === 0 && (
          <div style={{
            padding: '12px 16px', borderRadius: 8, marginBottom: 16,
            background: 'var(--surface-2, #1e293b)', border: '1px solid var(--border)',
          }}>
            <p style={{ margin: 0, fontWeight: 600, fontSize: 13 }}>
              No new eligible PRs found.
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
              All provided URLs have either already been imported, contain no Jira ticket key
              in their title, or are not valid PR URLs.
            </p>
          </div>
        )}

        {parseResult && (
          <div style={{ marginBottom: 16 }}>
            <InfoPanel title="Already processed — will be skipped" count={alreadyDone.length} variant="info">
              <UrlList items={alreadyDone} labelKey="pr_url" secondaryKey="jira_ticket_id" />
            </InfoPanel>
            <InfoPanel title="No Jira ticket found in PR title" count={noJiraKey.length} variant="warning">
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '8px 0 4px' }}>
                These PRs cannot be auto-imported because no Jira key (e.g. EN-3342) was found in their title.
              </p>
              <UrlList items={noJiraKey} labelKey="pr_url" secondaryKey="pr_title" />
            </InfoPanel>
            <InfoPanel title="Invalid / unrecognised URLs" count={invalidUrls.length} variant="error">
              <UrlList items={invalidUrls} />
            </InfoPanel>
          </div>
        )}

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 16 }}>
            <IconWarning /> {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            className="btn btn-primary"
            onClick={handleParse}
            disabled={parsing || !prInput.trim()}
          >
            {parsing ? 'Parsing…' : 'Parse PRs'}
          </button>
        </div>
      </div>
    );
  }

  // ── Step 2 ────────────────────────────────────────────────────────────────

  function renderStep2() {
    return (
      <div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6 }}>
          Found <strong>{eligible.length}</strong> eligible PR{eligible.length !== 1 ? 's' : ''} across{' '}
          <strong>{repositories.length}</strong> repositor{repositories.length !== 1 ? 'ies' : 'y'}.
          {unmappedRepos.length > 0
            ? ` ${unmappedRepos.length} of them ${unmappedRepos.length === 1 ? 'has' : 'have'} no product yet.`
            : ' All of them already have a product.'}
        </p>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 20 }}>
          A repository belongs to one product, and every feature imported from its PRs lands
          there. The answer is stored on the repository, so each repo is asked about once — you
          can change it later on the Repositories page.
        </p>

        {/* Repository → product mapping table */}
        <div className="card" style={{ marginBottom: 20, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, width: '45%' }}>
                  Repository
                </th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, width: '10%' }}>
                  PRs
                </th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>
                  Product
                </th>
              </tr>
            </thead>
            <tbody>
              {repositories.map(repo => (
                <tr key={repoKey(repo)} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 16px' }}>
                    <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {repo.repo_full_name}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                      {repo.provider}
                      {repo.product_id && ' · already mapped'}
                      {!repo.known && ' · not synced yet, will be added'}
                    </div>
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--text-secondary)' }}>
                    {repo.pr_count}
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <select
                      className="form-input"
                      style={{ margin: 0, fontSize: 13, maxWidth: 300 }}
                      value={mappings[repoKey(repo)] || ''}
                      onChange={e => setMappings(prev => ({
                        ...prev,
                        [repoKey(repo)]: e.target.value,
                      }))}
                    >
                      <option value="">— select product —</option>
                      {products.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* PRs with no Jira key — informational */}
        {noJiraKey.length > 0 && (
          <InfoPanel
            title={`PRs with no Jira ticket — cannot be imported (${noJiraKey.length})`}
            count={noJiraKey.length}
            variant="warning"
            defaultOpen
          >
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '8px 0 4px' }}>
              The following PRs will not be imported because no Jira ticket key was found in their title.
              You can manually create features for them after the wizard completes.
            </p>
            <UrlList items={noJiraKey} labelKey="pr_url" secondaryKey="pr_title" />
          </InfoPanel>
        )}

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 16 }}>
            <IconWarning /> {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={() => { setStep(1); setError(''); }}>
            <IconArrowLeft /> Back
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSaveMappingsAndPreview}
            disabled={savingMappings || !allReposMapped()}
          >
            {savingMappings ? 'Saving…' : 'Preview Import'}
          </button>
        </div>
      </div>
    );
  }

  // ── Step 3 ────────────────────────────────────────────────────────────────

  function renderStep3() {
    // Jira or Anthropic missing: nothing can be imported, so say so plainly
    // instead of showing rows built from PR titles.
    if (configError) {
      const isJira = configError.reason === 'jira_not_configured';
      return (
        <div>
          <div style={{
            padding: '16px 18px', borderRadius: 8, marginBottom: 20,
            background: 'var(--error-bg, #450a0a)', border: '1px solid var(--error, #ef4444)',
          }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600, fontSize: 14, color: 'var(--error, #ef4444)', marginBottom: 8 }}>
              <IconWarning /> {isJira ? 'Jira is not configured' : 'Anthropic is not configured'}
            </div>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-secondary)' }}>
              {configError.message}
            </p>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
              Nothing was imported. PR import will not fall back to the PR title or the raw ticket
              text, because that is what puts developer tasks into the feature registry.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => { setStep(2); setConfigError(null); }}>
              <IconArrowLeft /> Back
            </button>
            <Link className="btn btn-primary" to="/settings/ai">Open AI Settings</Link>
          </div>
        </div>
      );
    }

    if (previewing) {
      return (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
          <div style={{ marginBottom: 16, fontSize: 14 }}>
            Reading each Jira ticket and turning it into a catalogue entry with Claude…
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Each ticket is one model call; a batch of thirty takes about a minute.
          </p>
        </div>
      );
    }

    const needsReview = previewRows.filter(r => r.needs_review).length;
    const attachCount = selectedRows.filter(r => r.action === 'attach_to_existing').length;
    const createCount = selectedRows.length - attachCount;

    return (
      <div>
        {jiraAuthError && (
          <div style={{
            marginBottom: 12, padding: '10px 14px',
            background: 'var(--error-bg, #450a0a)', border: '1px solid var(--error, #ef4444)',
            borderRadius: 8, display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13,
          }}>
            <IconWarning />
            <span>
              Jira rejected the credentials (401). Every ticket it refused is listed below and
              cannot be imported.{' '}
              <Link to="/settings/ai" style={{ color: 'var(--accent)' }}>Fix in AI Settings</Link>
            </span>
          </div>
        )}
        {!embeddingsConfigured && (
          <div style={{
            marginBottom: 12, padding: '10px 14px',
            background: 'var(--info-bg, #0c1a2e)', border: '1px solid var(--accent)',
            borderRadius: 8, display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13,
          }}>
            <IconInfo />
            <span>
              No embeddings key — related existing features could not be looked up, so every row
              defaults to a new feature. Bug fixes are best attached to the feature they belong to;{' '}
              <Link to="/settings/ai" style={{ color: 'var(--accent)' }}>add the OpenAI embeddings key</Link>{' '}
              to get those suggestions.
            </span>
          </div>
        )}

        {/* PRs that cannot be imported. No placeholder rows are offered for these. */}
        {failedRows.length > 0 && (
          <InfoPanel
            title="Cannot be imported"
            count={failedRows.length}
            variant="error"
            defaultOpen
          >
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '8px 0 8px' }}>
              Either the ticket behind the PR could not be read, or its repository has no product
              to file the feature under. These are left out rather than imported from the PR title
              or filed under a guess. Fix the cause and re-run the preview, or create these
              features by hand.
            </p>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: 12 }}>
              {failedRows.map((f, i) => (
                <li key={i} style={{ padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--error, #ef4444)' }}>
                      {f.jira_ticket_id || '(no ticket key)'}
                    </span>
                    <a href={f.pr_url} target="_blank" rel="noreferrer"
                      style={{ color: 'var(--accent)', fontSize: 11, fontFamily: 'monospace' }}>
                      {(f.pr_url || '').replace(/^https?:\/\//, '').slice(0, 46)}…
                    </a>
                    {f.repo_full_name && (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                        {f.repo_full_name}
                      </span>
                    )}
                  </div>
                  <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>
                    {f.hint || f.detail || f.reason}
                  </div>
                </li>
              ))}
            </ul>
          </InfoPanel>
        )}

        {/* Summary strip */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center',
          fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12,
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconInfo />
            <strong>{selectedRows.length}</strong> of {previewRows.length} importable selected —
            {' '}{createCount} new feature{createCount !== 1 ? 's' : ''},
            {' '}{attachCount} attached to existing.
          </span>
          {needsReview > 0 && (
            <span style={{ color: 'var(--warning, #f59e0b)' }}>
              {needsReview} need{needsReview === 1 ? 's' : ''} review
            </span>
          )}
          {previewMeta?.model && (
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
              {previewMeta.model}
              {previewMeta.cost_usd ? ` · $${Number(previewMeta.cost_usd).toFixed(4)}` : ''}
            </span>
          )}
          <span style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="btn btn-ghost btn-sm"
              onClick={() => setPreviewRows(rows => rows.map(r => ({ ...r, selected: true })))}>
              Select all
            </button>
            <button type="button" className="btn btn-ghost btn-sm"
              onClick={() => setPreviewRows(rows => rows.map(r => ({ ...r, selected: false })))}>
              Clear
            </button>
          </span>
        </div>

        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px' }}>
          Each row is the catalogue entry Claude derived from the ticket and PR — the capability,
          not the task. Edit any field; choose whether it becomes a new feature or attaches the PR
          to an existing one. Rows flagged for review still read like a task or had no usable ticket.
        </p>

        {/* Preview rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          {previewRows.map((row, i) => (
            <PreviewRow
              key={row.pr_url || i}
              row={row}
              onToggle={() => updateRow(i, { selected: !row.selected })}
              onChange={patch => updateRow(i, patch)}
              onPlacement={value => setPlacement(i, value)}
            />
          ))}
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 16 }}>
            <IconWarning /> {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={() => { setStep(2); setError(''); }}>
            <IconArrowLeft /> Back
          </button>
          <button
            className="btn btn-primary"
            onClick={handleExecute}
            disabled={executing || selectedRows.length === 0}
          >
            {executing ? 'Importing…' : `Import ${selectedRows.length} Row${selectedRows.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    );
  }

  // ── Step 4 ────────────────────────────────────────────────────────────────

  function renderStep4() {
    if (!importResult) return null;

    const { imported, skipped, summary } = importResult;
    const attached = importResult.attached || [];
    const noJiraTotal = noJiraKey.length;

    return (
      <div>
        {/* Summary card */}
        <div className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--success, #22c55e)' }}>
                {summary.imported_count}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>New features created</div>
            </div>
            {summary.attached_count > 0 && (
              <div>
                <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--accent)' }}>
                  {summary.attached_count}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>PRs attached to existing features</div>
              </div>
            )}
            {summary.files_mapped > 0 && (
              <div>
                <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--accent)' }}>
                  {summary.files_mapped}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Code files mapped</div>
              </div>
            )}
            {summary.skipped_count > 0 && (
              <div>
                <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-secondary)' }}>
                  {summary.skipped_count}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Skipped</div>
              </div>
            )}
            {noJiraTotal > 0 && (
              <div>
                <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--warning, #f59e0b)' }}>
                  {noJiraTotal}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>PRs with no Jira ticket</div>
              </div>
            )}
          </div>
        </div>

        {/* Blast-radius info note */}
        {summary.files_mapped > 0 ? (
          <div style={{
            marginBottom: 20, padding: '12px 16px', borderRadius: 8, fontSize: 13,
            background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)',
            color: 'var(--text-secondary)',
          }}>
            <strong style={{ color: 'var(--accent)' }}>Blast Radius ready.</strong>{' '}
            {summary.files_mapped} code file mapping{summary.files_mapped !== 1 ? 's' : ''} were created from the source PRs.
            These features will now appear in blast radius analysis whenever those files are changed.
          </div>
        ) : (summary.imported_count > 0 || summary.attached_count > 0) ? (
          <div style={{
            marginBottom: 20, padding: '12px 16px', borderRadius: 8, fontSize: 13,
            background: 'var(--warning-bg, #451a03)', border: '1px solid var(--warning, #f59e0b)',
            color: 'var(--text-secondary)',
          }}>
            <strong style={{ color: 'var(--warning, #f59e0b)' }}>No code files mapped.</strong>{' '}
            The SCM API did not return file lists for these PRs. Features were imported but will not appear in
            blast radius analysis until code mappings are added manually via the feature detail page.
          </div>
        ) : null}

        {/* PRs with no Jira ticket */}
        {noJiraTotal > 0 && (
          <div style={{
            marginBottom: 20, padding: '14px 16px', borderRadius: 8,
            background: 'var(--warning-bg, #451a03)', border: '1px solid var(--warning, #f59e0b)',
          }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, fontWeight: 600, fontSize: 13, color: 'var(--warning, #f59e0b)' }}>
              <IconWarning />
              PRs that could not be auto-imported ({noJiraTotal})
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 8px' }}>
              No Jira ticket key was found in the title of these PRs. You can create features for them manually.
            </p>
            <UrlList items={noJiraKey} labelKey="pr_url" secondaryKey="pr_title" />
          </div>
        )}

        {/* Attached PRs list */}
        {attached.length > 0 && (
          <InfoPanel title="PRs attached to existing features" count={attached.length} variant="info" defaultOpen={attached.length <= 10}>
            <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', fontSize: 12 }}>
              {attached.slice(0, 30).map((f, i) => (
                <li key={i} style={{
                  padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
                  display: 'flex', gap: 10,
                }}>
                  <span style={{ fontFamily: 'monospace', color: 'var(--accent)', flexShrink: 0 }}>
                    {f.feature_id}
                  </span>
                  <span style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 11 }}>
                    {f.jira_ticket_id} · {f.files_mapped} file{f.files_mapped !== 1 ? 's' : ''} mapped
                  </span>
                </li>
              ))}
              {attached.length > 30 && (
                <li style={{ padding: '6px 0', color: 'var(--text-muted)', fontSize: 11 }}>
                  …and {attached.length - 30} more
                </li>
              )}
            </ul>
          </InfoPanel>
        )}

        {/* Skipped rows, with why */}
        {skipped.length > 0 && (
          <InfoPanel title="Skipped rows" count={skipped.length} variant="warning" defaultOpen>
            <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', fontSize: 12 }}>
              {skipped.map((s, i) => (
                <li key={i} style={{ padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: 10 }}>
                  <span style={{ fontFamily: 'monospace', color: 'var(--text-muted)', wordBreak: 'break-all' }}>
                    {(s.pr_url || '').replace(/^https?:\/\//, '')}
                  </span>
                  <span style={{ color: 'var(--warning, #f59e0b)', flexShrink: 0 }}>
                    {s.reason === 'already_processed' ? 'imported by another run meanwhile' :
                     s.reason === 'empty_major_feature' ? 'no major feature name' :
                     s.reason === 'related_feature_not_found' ? `existing feature ${s.related_feature_id || ''} not found` :
                     s.reason}
                  </span>
                </li>
              ))}
            </ul>
          </InfoPanel>
        )}

        {/* Imported features list */}
        {imported.length > 0 && (
          <InfoPanel title="New features created" count={imported.length} variant="success" defaultOpen={imported.length <= 10}>
            <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', fontSize: 12 }}>
              {imported.slice(0, 30).map((f, i) => (
                <li key={i} style={{
                  padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
                  display: 'flex', gap: 10,
                }}>
                  <span style={{ fontFamily: 'monospace', color: 'var(--accent)', flexShrink: 0 }}>
                    {f.feature_id}
                  </span>
                  <span style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 11 }}>
                    {f.jira_ticket_id}
                  </span>
                </li>
              ))}
              {imported.length > 30 && (
                <li style={{ padding: '6px 0', color: 'var(--text-muted)', fontSize: 11 }}>
                  …and {imported.length - 30} more
                </li>
              )}
            </ul>
          </InfoPanel>
        )}

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 16 }}>
            <IconWarning /> {error}
          </div>
        )}

        {/* Rollback section */}
        {!rolledBack ? (
          <div style={{
            marginTop: 24, padding: '14px 16px', borderRadius: 8,
            background: 'var(--surface-2, #1e293b)', border: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>Undo this import</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                Deletes the {summary.imported_count} feature{summary.imported_count !== 1 ? 's' : ''} created in this run
                {summary.attached_count > 0 ? ` and unmaps the ${summary.attached_count} attached PR${summary.attached_count !== 1 ? 's' : ''}` : ''}.
                The same PR URLs will become eligible for re-import.
              </div>
            </div>
            <button
              className="btn btn-ghost"
              style={{ borderColor: 'var(--error, #ef4444)', color: 'var(--error, #ef4444)' }}
              onClick={handleRollback}
              disabled={rollingBack}
            >
              <IconUndo /> {rollingBack ? 'Rolling back…' : 'Rollback Import'}
            </button>
          </div>
        ) : (
          <div style={{
            marginTop: 24, padding: '14px 16px', borderRadius: 8,
            background: 'var(--success-bg, #052e16)', border: '1px solid var(--success, #22c55e)',
            display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: 'var(--success, #22c55e)',
          }}>
            <IconCheck />
            Rollback complete. The features created in this run were deleted and attached PRs unmapped.
            The PR URLs are now eligible for re-import.
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="btn btn-ghost" onClick={() => navigate('/features')}>
            Go to Features
          </button>
          <button className="btn btn-primary" onClick={() => {
            setStep(1); setPrInput(''); setParseResult(null);
            setPreviewRows([]); setPreviewMeta(null); setImportResult(null);
            setFailedRows([]); setConfigError(null);
            setRolledBack(false); setError('');
          }}>
            Start New Import
          </button>
        </div>
      </div>
    );
  }

  // ── Layout ────────────────────────────────────────────────────────────────

  return (
    <div className="page-content">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/features')}>
            <IconArrowLeft /> Features
          </button>
          <div>
            <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <IconPR /> Import from Pull Requests
            </h2>
            <p className="page-subtitle">
              Paste PR URLs; each Jira ticket is turned into a catalogue entry — the capability, not the task — for you to review before import.
            </p>
          </div>
          {!cleanupMode && (
            <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
              onClick={() => { setCleanupMode(true); setError(''); }}
              title="Re-run the normaliser on features imported before it existed">
              Clean up earlier imports
            </button>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        {cleanupMode ? (
          <div className="card" style={{ padding: '24px 28px' }}>
            <RenormalizePanel products={products} onClose={() => setCleanupMode(false)} />
          </div>
        ) : (
          <>
            <StepIndicator current={step} />
            <div className="card" style={{ padding: '24px 28px' }}>
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
              {step === 4 && renderStep4()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
